import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, CATEGORY_COLORS } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, User, ArrowRight } from "lucide-react";

const SHIRT_SIZES = [
  { v: "YS", l: "Youth S" }, { v: "YM", l: "Youth M" }, { v: "YL", l: "Youth L" },
  { v: "AS", l: "Adult S" }, { v: "AM", l: "Adult M" }, { v: "AL", l: "Adult L" },
  { v: "AXL", l: "Adult XL" }, { v: "AXXL", l: "Adult XXL" },
];

export default function ParentCubers() {
  const { CATEGORY_LABEL } = useCategories();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/parent/children").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const requested = params.get("edit");
    if (requested && !showForm) {
      const child = rows.find((item) => item.id === requested);
      if (child) { setEditing(child); setShowForm(true); }
    }
  }, [rows, params, showForm]);
  const closeForm = () => {
    setShowForm(false); setEditing(null);
    const next = new URLSearchParams(params); next.delete("edit"); setParams(next, { replace: true });
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this cuber from your account?")) return;
    try {
      await api.delete(`/parent/children/${id}`);
      toast.success("Removed");
      await load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not remove this cuber."); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">My Cubers</h1>
          <p className="text-[#7A7D8C] mt-1 text-sm">Kids on your account. Add them here to make registration one-tap.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-child-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]"><Plus className="w-4 h-4" /> Add cuber</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="children-list">
        {rows.length === 0 && (
          <div className="col-span-full rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center text-[#7A7D8C]">
            <User className="w-8 h-8 mx-auto text-[#FF9F1C]" />
            <p className="mt-3">No cubers yet. Click <b>Add cuber</b> to get started.</p>
          </div>
        )}
        {rows.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col" data-testid={`child-${c.id}`}>
            <div className="flex items-start justify-between">
              <Link to={`/parent/cubers/${c.id}`} data-testid={`child-open-${c.id}`} className="flex-1 min-w-0 hover:opacity-70 transition-opacity">
                <div className="font-display font-black text-lg truncate">{c.full_name}</div>
                {c.current_school && <div className="text-xs text-[#7A7D8C] mt-0.5 truncate">{c.current_school}</div>}
              </Link>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`child-edit-${c.id}`} className="p-2 rounded-lg hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(c.id)} data-testid={`child-delete-${c.id}`} className="p-2 rounded-lg text-[#E71D36] hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {c.parent_selected_category && (
                <span className={`px-2.5 py-0.5 rounded-full font-bold border ${CATEGORY_COLORS[c.parent_selected_category] || ""}`}>{CATEGORY_LABEL[c.parent_selected_category]}</span>
              )}
              {c.shirt_size && <span className="bg-[#FDE74C]/40 text-[#7A5F00] px-2.5 py-0.5 rounded-full font-bold">{c.shirt_size}</span>}
            </div>
            <Link to={`/parent/cubers/${c.id}`} className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#FF9F1C] hover:underline">View stats <ArrowRight className="w-3 h-3" /></Link>
          </div>
        ))}
      </div>

      {showForm && (
        <ChildForm initial={editing} onClose={closeForm} onSaved={() => { closeForm(); load(); }} />
      )}
    </div>
  );
}

function ChildForm({ initial, onClose, onSaved }) {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const [f, setF] = useState({
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    current_school: initial?.current_school || "",
    shirt_size: initial?.shirt_size || "",
    parent_selected_category: initial?.parent_selected_category || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      if (!payload.parent_selected_category) delete payload.parent_selected_category;
      if (initial) { await api.patch(`/parent/children/${initial.id}`, payload); toast.success("Updated"); }
      else { await api.post("/parent/children", payload); toast.success("Added"); }
      onSaved();
    } catch (err) { const detail = err?.response?.data?.detail; toast.error(typeof detail === "string" ? detail : detail?.[0]?.msg || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto">
      <form onSubmit={save} className="bg-white rounded-3xl max-w-md w-full p-8 relative my-8" data-testid="child-form">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        <h2 className="font-display font-black text-2xl">{initial ? "Edit cuber" : "Add cuber"}</h2>
        <div className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name" value={f.first_name} required onChange={(v) => setF({ ...f, first_name: v })} tid="child-first-name" />
            <Field label="Last name" value={f.last_name} required onChange={(v) => setF({ ...f, last_name: v })} tid="child-last-name" />
          </div>
          {initial?.name_parts_inferred && <p className="text-xs text-[#7A7D8C]" data-testid="child-name-split-note">This older profile stored a full name. Please check the first and last name split before saving.</p>}
          {initial && <p className="text-xs text-[#7A7D8C]" data-testid="child-name-sync-note">Name changes appear on all linked results and competition records. Their IDs and original registration details stay unchanged.</p>}
          <Field label="Current school" value={f.current_school} onChange={(v) => setF({ ...f, current_school: v })} tid="child-school" />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Shirt size</label>
            <select value={f.shirt_size} onChange={(e) => setF({ ...f, shirt_size: e.target.value })} data-testid="child-shirt" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              <option value="">— choose —</option>
              {SHIRT_SIZES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Default category (optional)</label>
            <select value={f.parent_selected_category} onChange={(e) => setF({ ...f, parent_selected_category: e.target.value })} data-testid="child-category" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              <option value="">— choose —</option>
              {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
            </select>
            <p className="mt-2 text-xs text-[#7A7D8C]">Prefills new registrations. You can choose a different category for each competition without changing this default.</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={saving} data-testid="child-save-btn" className="px-5 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, tid, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}{required && <span className="text-[#E71D36]"> *</span>}</label>
      <input data-testid={tid} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C] focus:border-[#FF9F1C]" />
    </div>
  );
}
